import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Image from '@atlaskit/image';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';

import { useCreateContentFromTemplate } from '@confluence/create-content-from-template/entry-points/useCreateContentFromTemplate.ts';

import { PTV2TemplateCard } from './PTV2TemplateCard.tsx';
import {
	defaultDarkImgSrc,
	defaultImgSrc,
	BLUEPRINT_NAME_TO_IMG_MAPPING,
	BLUEPRINT_IMG_MAP_DARK,
} from './mappings/templateToImgMappings.ts';
import { usePTV2State } from './usePTV2Store.tsx';
import type { BLUEPRINT_NAMES } from './mappings/blueprintNameMapping.tsx';
import {
	BLUEPRINT_NAME_TO_IDS_MAPPING,
	type TemplateInfoType,
} from './mappings/blueprintNameMapping.ts';

export const i18n = defineMessages({
	pageTemplateType: {
		id: 'experiment-personalized-templates-v2.page.template',
		defaultMessage: 'Page template',
		description: 'Subheading on a card if the template suggestion is for page content type',
	},
	documentTemplateType: {
		id: 'experiment-personalized-templates-v2.document.template',
		defaultMessage: 'Document template',
		description: 'Subheading on a card if the template suggestion is for live page content type',
	},
});

type PTV2BlueprintTemplateCardType = {
	templateName: BLUEPRINT_NAMES;
	onClick: () => void;
	usage: number;
	blueprintModuleKeyToTemplate: Record<string, TemplateInfoType>;
};
export const PTV2BlueprintTemplateCard = ({
	usage,
	templateName,
	onClick,
	blueprintModuleKeyToTemplate,
}: PTV2BlueprintTemplateCardType) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { personalSpaceKey, personalizationHeuristic, onboardingPeriodDay, isXflow, source } =
		usePTV2State();
	const createFromTemplate = useCreateContentFromTemplate();

	const blueprintIds = BLUEPRINT_NAME_TO_IDS_MAPPING[templateName];

	if (!blueprintIds) {
		return null;
	}
	const { blueprintModuleCompleteKey } = blueprintIds;
	const template = blueprintModuleKeyToTemplate[blueprintModuleCompleteKey];
	if (!template || !template?.name) {
		return null;
	}
	const handleClick = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'templateCardPTV2',
				source: source ?? 'cc-onboarding-personalized-templates-v2',
				attributes: {
					templateType: 'blueprint',
					templateId: blueprintModuleCompleteKey.substring(
						blueprintModuleCompleteKey.lastIndexOf(':') + 1,
					),
					personalizationHeuristic,
					onboardingPeriodDay,
					isXflow,
				},
			},
		}).fire();
		onClick();
		try {
			await createFromTemplate(template, personalSpaceKey, {
				subType: 'live',
			});
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'created',
					actionSubject: 'template',
					actionSubjectId: template?.name,
					source: 'cc-onboarding-personalized-templates-v2',
					attributes: {
						isXflow,
					},
				},
			}).fire();
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'failed',
					actionSubject: 'template',
					actionSubjectId: template?.name,
					source: 'cc-onboarding-personalized-templates-v2',
					attributes: {
						errorMessage: error.message,
						isXflow,
					},
				},
			}).fire();
		}
	};

	return (
		<PTV2TemplateCard
			usage={usage}
			title={template?.name} // This should already be localized
			subTitle={formatMessage(i18n.documentTemplateType)}
			icon={<PageLiveDocIcon label="" />}
			onClick={handleClick}
			image={
				<Image
					src={BLUEPRINT_NAME_TO_IMG_MAPPING[templateName as BLUEPRINT_NAMES] || defaultImgSrc}
					srcDark={BLUEPRINT_IMG_MAP_DARK[templateName as BLUEPRINT_NAMES] || defaultDarkImgSrc}
					alt=""
				/>
			}
		/>
	);
};
